import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  User,
  Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ProjectCardData } from './ProjectCard';
import { ProjectStatusPill } from './ProjectStatusPill';
import { ProjectProgressBar } from './ProjectProgressBar';
import { ProjectTaskRows } from './ProjectTaskRows';
import { TaskDialog, TaskDialogTask } from './TaskDialog';
import { formatDate } from './projectUtils';

interface OpenedTask {
  projectId: string;
  task: TaskDialogTask;
  columns: Array<{ id: string; name: string }>;
}

interface ProjectsTableProps {
  items: ProjectCardData[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  currentUserId?: string;
}

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'updatedAt', label: 'Ostatnia zmiana' },
  { value: 'name', label: 'Nazwa' },
  { value: 'status', label: 'Status' },
  { value: 'startDate', label: 'Data startu' },
  { value: 'plannedEndDate', label: 'Termin' },
  { value: 'createdAt', label: 'Data utworzenia' },
];

export const ProjectsTable: React.FC<ProjectsTableProps> = ({
  items,
  sortBy,
  sortOrder,
  onSortChange,
  currentUserId,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [openedTask, setOpenedTask] = useState<OpenedTask | null>(null);

  const handleOpenTask: React.ComponentProps<
    typeof ProjectTaskRows
  >['onOpenTask'] = (projectId, task, columns) => {
    setOpenedTask({ projectId, task, columns });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allExpanded = items.length > 0 && items.every((p) => expanded.has(p.id));

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpanded(new Set());
    } else {
      setExpanded(new Set(items.map((p) => p.id)));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            Sortuj wg:
          </span>
          <Select
            value={sortBy}
            onValueChange={(value) => onSortChange(value, sortOrder)}
          >
            <SelectTrigger className="h-8 w-[180px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')
            }
            className="h-8 px-2"
            title={sortOrder === 'asc' ? 'Rosnąco' : 'Malejąco'}
          >
            {sortOrder === 'asc' ? (
              <ArrowUp className="w-4 h-4" />
            ) : (
              <ArrowDown className="w-4 h-4" />
            )}
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleExpandAll}
          disabled={items.length === 0}
          className="text-xs h-7"
        >
          {allExpanded ? (
            <>
              <ChevronsDownUp className="w-4 h-4 mr-1" />
              Zwiń wszystkie
            </>
          ) : (
            <>
              <ChevronsUpDown className="w-4 h-4 mr-1" />
              Rozwiń wszystkie
            </>
          )}
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((p) => {
          const isOpen = expanded.has(p.id);
          const leaderName = p.leader
            ? `${p.leader.firstName} ${p.leader.lastName}`
            : 'Brak lidera';
          const hasDates = p.startDate || p.plannedEndDate;
          return (
            <Card key={p.id} className="overflow-hidden">
              <div
                onClick={() => navigate(`/projects/${p.id}`)}
                className="p-4 cursor-pointer hover:bg-accent/20 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(p.id);
                    }}
                    className="p-1 -m-1 rounded hover:bg-muted text-muted-foreground shrink-0 mt-0.5"
                    aria-label={isOpen ? 'Zwiń zadania' : 'Rozwiń zadania'}
                  >
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <h3
                        className="font-semibold text-base leading-tight truncate"
                        title={p.name}
                      >
                        {p.name}
                      </h3>
                      <ProjectStatusPill status={p.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        {leaderName}
                      </span>
                      {hasDates && (
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-3.5 h-3.5" />
                          {formatDate(p.startDate)} – {formatDate(p.plannedEndDate)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {p.memberCount ?? 0} czł.
                      </span>
                      <div className="min-w-[160px]">
                        <ProjectProgressBar
                          taskCount={p.taskCount ?? 0}
                          doneTaskCount={p.doneTaskCount ?? 0}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className="border-t bg-muted/30 px-4 py-3">
                  <ProjectTaskRows
                    projectId={p.id}
                    onOpenTask={handleOpenTask}
                  />
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {openedTask && (
        <TaskDialog
          open
          onClose={() => setOpenedTask(null)}
          projectId={openedTask.projectId}
          task={openedTask.task}
          columns={openedTask.columns}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
};
